import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { get, post } from '../utils/api';
import { clearPortalSessionStorage } from '../utils/sessionInvalidation';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load existing session (prefer header token, but backend also reads HttpOnly cookie)
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        // Try /api/me - backend can read HttpOnly cookie even if sessionStorage is empty
        // This works because HttpOnly cookies are automatically sent with requests
        const me = await get('/api/me');
        if (me?.staff) {
          setStaff(me.staff);
          // Restore sessionStorage token if we got a valid session (for header-based auth)
          // The cookie will still work, but having it in sessionStorage helps with header-based requests
          try {
            const existingToken = sessionStorage.getItem('session_token');
            // If we don't have a token or it's just 'cookie', try to get a fresh one
            // Note: The cookie-based session should work regardless, but we keep sessionStorage
            // in sync for consistency
            if (!existingToken || existingToken === 'cookie') {
              // Keep 'cookie' marker to indicate cookie-based session is active
              sessionStorage.setItem('session_token', 'cookie');
            }
          } catch {}
        } else {
          clearPortalSessionStorage();
          setStaff(null);
        }
      } catch (e) {
        // Session invalid or expired - clear state
        console.warn('[AuthContext] Session restore failed:', e?.message || e);
        clearPortalSessionStorage();
        setStaff(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const requestOtp = useCallback(async (email, recaptchaToken = null) => {
    // Backend sends OTP via email only (secure design - no code in response)
    const base = process.env.REACT_APP_API_BASE_URL || '';
    const url = `${base.replace(/\/$/, '')}/request-otp`;
    const body = { email };
    if (recaptchaToken) {
      body.recaptchaToken = recaptchaToken;
    }
    const r = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    if (!r.ok) {
      const errorData = await r.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to send OTP');
    }
    const data = await r.json();
    // Response: { success: true, message: 'OTP sent to your email' }
    // NO CODE in response (security: OTP only accessible via email)
    return data;
  }, []);

  // Verify OTP via backend (true validation - no bypass)
  const verifyOtpLocal = useCallback(async ({ email, userInputCode }) => {
    try {
      const resp = await post('/api/verify-otp', { email, code: userInputCode || '' });
      // Persist header token if provided; server also sets HttpOnly cookie
      if (resp?.session_token) {
        try { sessionStorage.setItem('session_token', resp.session_token); } catch {}
      }
      // Use returned staff details immediately so UI (e.g., Users button) can render
      if (resp?.staff) { setStaff(resp.staff); return { staff: resp.staff }; }
      // Fallback to /api/me if response didn't include staff
      const me = await get('/api/me');
      if (me?.staff) { setStaff(me.staff); return { staff: me.staff }; }
      throw new Error('Verification failed');
    } catch (e) {
      console.warn('verify-otp error:', e?.message || e);
      throw e;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await post('/api/logout');
    } catch {}
    setStaff(null);
    clearPortalSessionStorage();
  }, []);

  const value = useMemo(() => ({ staff, loading, requestOtp, verifyOtpLocal, logout, isAuthenticated: !!staff }), [staff, loading, requestOtp, verifyOtpLocal, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
